import { Alert, Flex } from "antd";
import { useContext, useEffect, useMemo } from "react";
import StripeContext from "../../../../contexts/stripe/StripeContext";
import { ContextStatus } from "@leedpulse-monorepo/enums";
import { Elements } from '@stripe/react-stripe-js';
import { LoadingOutlined } from "@ant-design/icons";
import { colors } from "../../../../../domain/types/colors";
import StripePaymentForm from "./StripePaymentForm";
import { Authentication } from "@leedpulse-monorepo/classes";
import { UserRole } from "@leedpulse-monorepo/enums";

interface StripePaymentProps {
    planId: string;
    companyId: string;
    companyApiKey: string;
    userUid: string;
    professionalNumber: string;
}

const StripePayment: React.FC<StripePaymentProps> = ({ planId, companyId, companyApiKey, userUid, professionalNumber }) => {
    const { clientSecret, stripePromise, loadingStatus: status, initializePayment } = useContext(StripeContext);

    // Build a minimal Authentication object for the signup flow (no Firebase token yet)
    const auth = useMemo(
        () => new Authentication(userUid, '', UserRole.customer, ''),
        [userUid]
    );

    useEffect(() => {
        // Only need companyId for the SetupIntent (card collection)
        if (status === ContextStatus.LOADING || !companyId || clientSecret) return;
        initializePayment(companyId, companyApiKey, auth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    if (status === ContextStatus.LOADING) {
        return (
            <Flex justify="center" align="center" style={{ width: '100%', height: '100%' }}>
                <LoadingOutlined spin style={{ fontSize: "2rem" }} />
            </Flex>
        );
    } else if (status === ContextStatus.ERROR || !clientSecret) {
        return <Alert message="Erreur lors de l'initialisation du paiement" type="error" />;
    }

    return (
        <Flex vertical gap={8}>
            <Elements
                stripe={stripePromise}
                options={{
                    locale: 'fr', // TODO : @MrWarzo gérer le multi pays
                    appearance: { // Needed bc this doesnt work by classname (need a clean to use variables)
                        theme: 'flat',
                        variables: {
                            colorPrimary: colors.primary.main,
                            fontFamily: 'Work Sans, sans-serif',
                            colorText: '#2D3436',
                            colorBackground: '#ffffff',
                            borderRadius: '6px',
                            fontSizeBase: '14px',
                            spacingUnit: '4px',
                            colorDanger: '#EF4444',
                        },
                        rules: {
                            // Match Ant Design input style exactly
                            '.Input': {
                                backgroundColor: '#ffffff',
                                border: '1px solid #E6F7F0',
                                borderRadius: '6px',
                                padding: '8px 11px',
                                fontSize: '14px',
                                fontFamily: 'Work Sans, sans-serif',
                                lineHeight: '1.5714285714285714',
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                boxShadow: 'none',
                                color: '#2D3436',
                            },
                            '.Input:hover': {
                                borderColor: colors.primary.main,
                            },
                            '.Input:focus': {
                                borderColor: '#22c55e',
                                boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.1)',
                            },
                            '.Input--invalid': {
                                borderColor: '#EF4444',
                            },
                            '.Label': {
                                fontFamily: 'Work Sans, sans-serif',
                                fontSize: '14px',
                                fontWeight: '400',
                                color: '#2D3436',
                                marginBottom: '4px',
                            },
                            '.Error': {
                                fontFamily: 'Work Sans, sans-serif',
                                fontSize: '12px',
                                color: '#EF4444',
                            },
                        },
                    },
                }}
            >
                <StripePaymentForm
                    planId={planId}
                    companyId={companyId}
                    companyApiKey={companyApiKey}
                    auth={auth}
                    professionalNumber={professionalNumber}
                />
            </Elements>
        </Flex>
    );
};

export default StripePayment;
